// CategoryProductsSection.js - With Unique Class Names (cpw- prefix)
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import "./CategoryProductsSection.css";

const CategoryProductsSection = () => {
  const [categories, setCategories] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [visibleProducts, setVisibleProducts] = useState({});
  const [discountPercentage, setDiscountPercentage] = useState(0);
  const [quantities, setQuantities] = useState({});
  const [activeCart, setActiveCart] = useState({});
  const [inputValues, setInputValues] = useState({});
  const navigate = useNavigate();

  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

  // Fetch discount percentage
  useEffect(() => {
    const fetchDiscount = async () => {
      try {
        const response = await axios.post(`${API_BASE_URL}home.php`);
        if (response.data && response.data.body) {
          const discountValue = response.data.body.discount_value?.[0]?.discount_value || 0;
          setDiscountPercentage(parseFloat(discountValue));
        }
      } catch (error) {
        console.error("Error fetching discount:", error);
      }
    };
    fetchDiscount();
  }, [API_BASE_URL]);

  useEffect(() => {
    const fetchCategoriesWithProducts = async () => {
      try {
        setIsLoading(true);
        const response = await axios.post(`${API_BASE_URL}category_with_products.php`);

        if (response.data && response.data.head && response.data.head.code === 200) {
          const data = response.data.body || [];
          
          const filteredData = data.filter(category => 
            category.front_active === "1" || category.front_active === 1
          );
          
          setCategories(filteredData);
          
          const initialVisibility = {};
          const initialQuantities = {};
          const initialActive = {};
          const initialInputValues = {};
          const cartItems = JSON.parse(localStorage.getItem("cartItems") || "[]");
          
          filteredData.forEach(cat => {
            initialVisibility[cat.category_id] = false;
            cat.products.forEach(product => {
              const existing = cartItems.find(item => item.product_id === product.product_id);
              initialQuantities[product.product_id] = existing ? existing.count : 1;
              initialActive[product.product_id] = existing ? true : false;
              initialInputValues[product.product_id] = existing ? existing.count.toString() : '';
            });
          });
          setVisibleProducts(initialVisibility);
          setQuantities(initialQuantities);
          setActiveCart(initialActive);
          setInputValues(initialInputValues);
          
          console.log("Categories with products loaded:", filteredData.length);
        } else {
          console.error("Failed to fetch categories:", response.data);
          setCategories([]);
        }
      } catch (error) {
        console.error("Error fetching categories with products:", error);
        setError("Failed to load categories");
        setCategories([]);
      } finally {
        setIsLoading(false);
      }
    };

    fetchCategoriesWithProducts();
  }, [API_BASE_URL]);

  // Sync with localStorage changes
  useEffect(() => {
    const handleStorageChange = () => {
      const cartItems = JSON.parse(localStorage.getItem("cartItems") || "[]");
      const newActive = {};
      const newQuantities = {};
      const newInputValues = {};
      categories.forEach(cat => {
        cat.products.forEach(product => {
          const existing = cartItems.find(item => item.product_id === product.product_id);
          newActive[product.product_id] = existing ? true : false;
          newQuantities[product.product_id] = existing ? existing.count : 1;
          newInputValues[product.product_id] = existing ? existing.count.toString() : '';
        });
      });
      setActiveCart(newActive);
      setQuantities(newQuantities);
      setInputValues(newInputValues);
    };

    window.addEventListener("storage", handleStorageChange);
    window.addEventListener("cartUpdated", handleStorageChange);
    
    return () => {
      window.removeEventListener("storage", handleStorageChange);
      window.removeEventListener("cartUpdated", handleStorageChange);
    };
  }, [categories]);

  // Toggle show all products
  const toggleShowAll = (categoryId) => {
    setVisibleProducts(prev => ({
      ...prev,
      [categoryId]: !prev[categoryId]
    }));
  };

  // Handle quantity change - like Shop page
  const handleQuantityChange = (productId, value) => {
    setInputValues(prev => ({ ...prev, [productId]: value }));
    
    if (value === '') {
      return;
    }
    
    const numValue = parseInt(value, 10);
    if (!isNaN(numValue) && numValue > 0) {
      setQuantities(prev => ({ ...prev, [productId]: numValue }));
      updateCartQuantity(productId, numValue);
    }
  };

  const handleQuantityBlur = (productId, value) => {
    if (value === '' || isNaN(parseInt(value, 10)) || parseInt(value, 10) < 1) {
      removeFromCart(productId);
      setQuantities(prev => ({ ...prev, [productId]: 1 }));
      setActiveCart(prev => ({ ...prev, [productId]: false }));
      setInputValues(prev => ({ ...prev, [productId]: '' }));
    }
  };

  // Remove from cart
  const removeFromCart = (productId) => {
    let cartItems = JSON.parse(localStorage.getItem("cartItems") || "[]");
    cartItems = cartItems.filter(item => item.product_id !== productId);
    localStorage.setItem("cartItems", JSON.stringify(cartItems));
    localStorage.removeItem(`count_${productId}`);
    
    const uniqueProductCount = cartItems.length;
    localStorage.setItem("cartCount", uniqueProductCount);
    
    window.dispatchEvent(new Event("cartUpdated"));
    window.dispatchEvent(new Event("storage"));
  };

  // Update cart quantity
  const updateCartQuantity = (productId, newQuantity) => {
    let cartItems = JSON.parse(localStorage.getItem("cartItems") || "[]");
    const existingIndex = cartItems.findIndex(item => item.product_id === productId);
    
    if (existingIndex !== -1) {
      cartItems[existingIndex].count = newQuantity;
      localStorage.setItem(`count_${productId}`, newQuantity);
    }
    
    localStorage.setItem("cartItems", JSON.stringify(cartItems));
    
    const uniqueProductCount = cartItems.length;
    localStorage.setItem("cartCount", uniqueProductCount);
    
    window.dispatchEvent(new Event("cartUpdated"));
    window.dispatchEvent(new Event("storage"));
  };

  // Handle add to cart (first time)
  const handleAddToCart = (product, categoryNonDiscount) => {
    const quantity = 1;
    
    const cartItems = JSON.parse(localStorage.getItem("cartItems") || "[]");
    const existingIndex = cartItems.findIndex(item => item.product_id === product.product_id);
    
    if (existingIndex !== -1) {
      return;
    }
    
    const mrp = parseFloat(product.mrp_rate) || 0;
    // Check if category is non-discount OR product is non-discount
    const isNonDiscount = categoryNonDiscount === "1" || categoryNonDiscount === 1 || 
                          product.is_non_discount === "1" || product.is_non_discount === 1;
    let discountedPrice = mrp;
    
    if (!isNonDiscount && discountPercentage > 0) {
      const discountAmount = (discountPercentage * mrp) / 100;
      discountedPrice = mrp - discountAmount;
    }
    
    cartItems.push({
      product_id: product.product_id,
      product_name: product.product_name,
      image: product.product_image,
      oldprice: mrp.toString(),
      price: discountedPrice.toString(),
      count: quantity,
      is_non_discount: product.is_non_discount || "0"
    });
    
    localStorage.setItem("cartItems", JSON.stringify(cartItems));
    localStorage.setItem(`count_${product.product_id}`, quantity);
    
    const uniqueProductCount = cartItems.length;
    localStorage.setItem("cartCount", uniqueProductCount);
    
    setActiveCart(prev => ({ ...prev, [product.product_id]: true }));
    setQuantities(prev => ({ ...prev, [product.product_id]: quantity }));
    setInputValues(prev => ({ ...prev, [product.product_id]: quantity.toString() }));
    
    window.dispatchEvent(new Event("cartUpdated"));
    window.dispatchEvent(new Event("storage"));
  };

  // Navigate to product detail
  const handleProductClick = (product) => {
    navigate(`/productdetail/${product.product_id}`);
  };

  // Get price with discount - check both category and product non-discount
  const getPriceWithDiscount = (mrpRate, isCategoryNonDiscount, isProductNonDiscount) => {
    const mrp = parseFloat(mrpRate) || 0;
    let discountedPrice = mrp;
    let hasDiscount = false;
    
    // Check if category is non-discount OR product is non-discount
    const isNonDiscount = isCategoryNonDiscount === "1" || isCategoryNonDiscount === 1 || 
                          isProductNonDiscount === "1" || isProductNonDiscount === 1;
    
    // Only apply discount if NOT non-discount and discountPercentage > 0
    if (!isNonDiscount && discountPercentage > 0) {
      const discountAmount = (discountPercentage * mrp) / 100;
      discountedPrice = mrp - discountAmount;
      hasDiscount = true;
    }
    
    return {
      original: Math.round(mrp * 100) / 100,
      discounted: Math.round(discountedPrice * 100) / 100,
      hasDiscount: hasDiscount,
      isNonDiscount: isNonDiscount
    };
  };

  const getImageUrl = (image) => {
    if (!image) return null;
    if (image.startsWith('http://') || image.startsWith('https://')) {
      return image;
    }
    return image;
  };

  if (isLoading) {
    return (
      <div className="cpw-section">
        <div className="cpw-container">
          <div className="cpw-header">
            <div className="cpw-badge">
              <span className="cpw-badge-icon">🌟</span>
              <span>Explore Our Collection</span>
            </div>
            <h2 className="cpw-title">
              Shop by
              <span className="cpw-title-gradient"> Category</span>
            </h2>
            <p className="cpw-subtitle">
              Discover amazing products across all categories
            </p>
          </div>
          <div className="cpw-loading">
            <div className="cpw-loading-icon">⏳</div>
            <div className="cpw-loading-text">Loading categories & products...</div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="cpw-section">
        <div className="cpw-container">
          <div className="cpw-header">
            <div className="cpw-badge">
              <span className="cpw-badge-icon">🌟</span>
              <span>Explore Our Collection</span>
            </div>
            <h2 className="cpw-title">
              Shop by
              <span className="cpw-title-gradient"> Category</span>
            </h2>
          </div>
          <div className="cpw-error">
            <div className="cpw-error-icon">⚠️</div>
            <h3 className="cpw-error-title">Unable to load collection</h3>
            <p className="cpw-error-text">Please try again later</p>
          </div>
        </div>
      </div>
    );
  }

  if (!categories || categories.length === 0) {
    return (
      <div className="cpw-section">
        <div className="cpw-container">
          <div className="cpw-header">
            <div className="cpw-badge">
              <span className="cpw-badge-icon">🌟</span>
              <span>Explore Our Collection</span>
            </div>
            <h2 className="cpw-title">
              Shop by
              <span className="cpw-title-gradient"> Category</span>
            </h2>
            <p className="cpw-subtitle">
              Discover amazing products across all categories
            </p>
          </div>
          <div className="cpw-empty">
            <div className="cpw-empty-icon">🛍️</div>
            <h3 className="cpw-empty-title">No featured categories available</h3>
            <p className="cpw-empty-text">Check back soon for our collection</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="cpw-section">
      <div className="cpw-container">
        <div className="cpw-header">
          <div className="cpw-badge">
            <span className="cpw-badge-icon">🌟</span>
            <span>Explore Our Collection</span>
          </div>
          <h2 className="cpw-title">
            Shop by
            <span className="cpw-title-gradient"> Category</span>
          </h2>
          <p className="cpw-subtitle">
            Discover amazing products across all categories
          </p>
        </div>

        {categories.map((category, index) => {
          const products = category.products || [];
          const showAll = visibleProducts[category.category_id] || false;
          const displayedProducts = showAll ? products : products.slice(0, 6);
          const hasMore = products.length > 6;
          // Get category non-discount status
          const categoryNonDiscount = category.non_discount || "0";

          return (
            <div 
              key={category.category_id} 
              className="cpw-category-section"
              style={{ animationDelay: `${index * 100}ms` }}
            >
              <div className="cpw-category-header">
                <div className="cpw-category-image">
                  <img
                    src={getImageUrl(category.category_image)}
                    alt={category.category_name}
                    onError={(e) => {
                      e.target.src = `${API_BASE_URL}uploads/noimages.jpg`;
                    }}
                  />
                </div>
                <div className="cpw-category-info">
                  <h3>{category.category_name}</h3>
                  <div className="cpw-product-count">
                    <span>{products.length}</span> products available
                  </div>
                </div>
              </div>

              {products.length > 0 ? (
                <>
                  <div className="cpw-products-grid">
                    {displayedProducts.map((product) => {
                      // Check if category is non-discount OR product is non-discount
                      const isCategoryNonDisc = categoryNonDiscount === "1" || categoryNonDiscount === 1;
                      const isProductNonDisc = product.is_non_discount === "1" || product.is_non_discount === 1;
                      const isNonDiscount = isCategoryNonDisc || isProductNonDisc;
                      
                      const priceInfo = getPriceWithDiscount(
                        product.mrp_rate, 
                        categoryNonDiscount, 
                        product.is_non_discount
                      );
                      const inputValue = inputValues[product.product_id] || '';
                      const isActive = activeCart[product.product_id] || false;
                      
                      return (
                        <div key={product.product_id} className="cpw-product-card">
                          <div className="cpw-product-image" onClick={() => handleProductClick(product)}>
                            {product.new_arrival === "1" && (
                              <div className="cpw-new-badge">✨ NEW</div>
                            )}
                            <div className="cpw-product-types">
                              {product.day === "1" && (
                                <span className="cpw-type-badge cpw-day">☀️ Day</span>
                              )}
                              {product.night === "1" && (
                                <span className="cpw-type-badge cpw-night">🌙 Night</span>
                              )}
                              {product.kids === "1" && (
                                <span className="cpw-type-badge cpw-kids">🧒 Kids</span>
                              )}
                            </div>
                            <img
                              src={getImageUrl(product.product_image)}
                              alt={product.product_name}
                              loading="lazy"
                              onError={(e) => {
                                e.target.src = `${API_BASE_URL}uploads/noimages.jpg`;
                              }}
                            />
                            <div className="cpw-product-overlay"></div>
                          </div>
                          
                          <div className="cpw-product-info">
                            <div className="cpw-product-name" onClick={() => handleProductClick(product)}>
                              {product.product_name}
                            </div>
                            <div className="cpw-product-price" onClick={() => handleProductClick(product)}>
                              {priceInfo.hasDiscount ? (
                                <>
                                  <del className="cpw-mrp-price">
                                    ₹{priceInfo.original.toFixed(2)}
                                  </del>
                                  <span className="cpw-online-price">
                                    ₹{priceInfo.discounted.toFixed(2)}
                                  </span>
                                </>
                              ) : (
                                <span className="cpw-online-price">
                                  ₹{priceInfo.original.toFixed(2)}
                                </span>
                              )}
                            </div>
                            
                            {/* Cart Section - Like Shop page */}
                            <div className="cpw-cart-section">
                              {!isActive ? (
                                <button 
                                  className="cpw-add-btn"
                                  onClick={() => handleAddToCart(product, categoryNonDiscount)}
                                >
                                  <i className="bi bi-cart-plus"></i>
                                  Add to Cart
                                </button>
                              ) : (
                                <div className="cpw-qty-control">
                                  <button 
                                    className="cpw-qty-btn"
                                    onClick={() => {
                                      const currentQty = parseInt(inputValue) || 1;
                                      const newQty = currentQty - 1;
                                      if (newQty <= 0) {
                                        removeFromCart(product.product_id);
                                        setActiveCart(prev => ({ ...prev, [product.product_id]: false }));
                                        setInputValues(prev => ({ ...prev, [product.product_id]: '' }));
                                        setQuantities(prev => ({ ...prev, [product.product_id]: 1 }));
                                      } else {
                                        setInputValues(prev => ({ ...prev, [product.product_id]: newQty.toString() }));
                                        setQuantities(prev => ({ ...prev, [product.product_id]: newQty }));
                                        updateCartQuantity(product.product_id, newQty);
                                      }
                                    }}
                                  >
                                    <b>-</b>
                                  </button>
                                  <input
                                    type="number"
                                    className="cpw-qty-input"
                                    value={inputValue}
                                    onChange={(e) => handleQuantityChange(product.product_id, e.target.value)}
                                    onBlur={(e) => handleQuantityBlur(product.product_id, e.target.value)}
                                    min="0"
                                    step="1"
                                    placeholder="0"
                                  />
                                  <button 
                                    className="cpw-qty-btn"
                                    onClick={() => {
                                      const currentQty = parseInt(inputValue) || 0;
                                      const newQty = currentQty + 1;
                                      setInputValues(prev => ({ ...prev, [product.product_id]: newQty.toString() }));
                                      setQuantities(prev => ({ ...prev, [product.product_id]: newQty }));
                                      updateCartQuantity(product.product_id, newQty);
                                    }}
                                  >
                                    <b>+</b>
                                  </button>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  {hasMore && (
                    <div className="cpw-show-more-container">
                      <button
                        className="cpw-show-more-btn"
                        onClick={() => toggleShowAll(category.category_id)}
                      >
                        {showAll ? 'Show Less ↑' : `Show All ${products.length} Products ↓`}
                      </button>
                    </div>
                  )}
                </>
              ) : (
                <div className="cpw-no-products">
                  <div className="cpw-no-products-icon">📦</div>
                  <div className="cpw-no-products-text">
                    No products available in this category
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default CategoryProductsSection;